import { faImagePolaroid } from '@fortawesome/pro-thin-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePermissions } from '@hooks/usePermissions';
import { useDeleteCADJob } from '@partful/features/job/lib/useDeleteCADJob';
import type { ProductListResult } from '@partful/shared-types';
import { classNames } from '@partful/shared/lib/classNames';
import { Badge } from '@partful/shared/ui/Badge';
import { Button } from '@partful/shared/ui/Button';
import { useConfirmation } from '@partful/shared/ui/Confirmation/useConfirmation';
import { useSnackBar } from '@partful/shared/ui/SnackBar/useSnackBar';
import { Typography } from '@partful/shared/ui/Typography';
import { useTenant } from '@providers/Tenant';
import { ComponentProps, MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { calculateProductCardDetails } from '../../lib/productStatusHelpers';
import { CardContent, CardMedia, NavCard } from '../Card';
import styles from './InstructionCard.module.scss';

type InstructionCardProps = {
  product: ProductListResult;
};

export function InstructionCard({
  product,
  className,
  ...rest
}: Omit<ComponentProps<typeof NavCard>, 'to'> & InstructionCardProps) {
  const { t: i18n } = useTranslation();

  const { status, simpleStatus, icon, destination } = calculateProductCardDetails(product);
  const [thumbnailNotFound, setThumbnailNotFound] = useState(false);

  const productComplete = simpleStatus === 'DRAFT' || simpleStatus === 'COMPLETED';
  const canDelete = usePermissions(['delete', 'product']);
  const canUpdate = usePermissions(['update', 'product']);
  const canManage = canDelete || canUpdate;

  const hasFailed = simpleStatus === 'FAILED';

  const { accountId } = useTenant();
  const { mutateAsync } = useDeleteCADJob();
  const { confirm, close, disable } = useConfirmation();
  const { showSnackBar } = useSnackBar();
  const handleDeleteProduct = (e: MouseEvent) => {
    e.preventDefault(); // Prevents the nav card click triggering.
    confirm({
      title: i18n('instructions:library.card.actions.delete.confirm.title', {
        name: product.name,
      }),
      message: i18n('instructions:library.card.actions.delete.confirm.message'),
      cancelText: i18n('instructions:common.cancel'),
      confirmText: i18n('instructions:common.delete'),
      onConfirm: async () => {
        try {
          disable();
          await mutateAsync({ accountId, jobId: product.runID! });
          close();
          showSnackBar({
            severity: 'success',
            message: i18n('instructions:library.card.actions.delete.success', { name: product.name }),
          });
        } catch (error) {
          /* istanbul ignore next - edge case */
          showSnackBar({
            severity: 'error',
            message: i18n('instructions:library.card.actions.delete.error', { name: product.name }),
          });
        }
      },
    });
  };

  return (
    <NavCard
      className={classNames(styles.card, className, hasFailed ? styles.failed : '')}
      to={destination}
      data-status={simpleStatus}
      onClick={(e) => {
        if (hasFailed) {
          e.preventDefault();
        }
      }}
      {...rest}
    >
      <CardMedia>
        {productComplete && thumbnailNotFound && (
          <div className={styles.statusDetails}>
            <FontAwesomeIcon icon={faImagePolaroid} />
            <Typography appearance="heading6">{i18n(`instructions:library.card.noImage.title`)}</Typography>
            <Typography appearance="label" textWrap="balance">
              {i18n(`instructions:library.card.noImage.description`)}
            </Typography>
          </div>
        )}
        {productComplete && !thumbnailNotFound && (
          <img
            src={product.thumbnailUrl}
            alt={product.name}
            className={styles.thumbnail}
            onError={() => setThumbnailNotFound(true)}
          />
        )}
        {!productComplete && (
          <div className={styles.statusDetails}>
            {icon && <FontAwesomeIcon icon={icon} />}
            <Typography appearance="heading6" textWrap="balance">
              {i18n([
                `instructions:library.card.${status}.title`,
                `instructions:library.card.${simpleStatus}.title`,
                '',
              ])}
            </Typography>
            <Typography appearance="label" textWrap="balance">
              {i18n(
                [
                  `instructions:library.card.${status}.description`,
                  `instructions:library.card.${simpleStatus}.description`,
                  '',
                ],
                { reference: product.runID }
              )}
            </Typography>
          </div>
        )}
        <Badge className={styles.statusChip}>{i18n(`instructions:library.card.statusLabel.${simpleStatus}`)}</Badge>
        {productComplete && canManage && (
          <Button
            linkComponent={Link}
            to={`/instructions/manage/${encodeURIComponent(product.configurationPID!)}`}
            size="small"
            name="instructions_product_library_manage_product_click"
            className={styles.manageButton}
            data-testid={`${product.productExternalID}-manage`}
          >
            {i18n(`instructions:common.manageProduct`)}
          </Button>
        )}
        {!productComplete && canDelete && (
          <Button
            onClick={handleDeleteProduct}
            size="small"
            variant="destructive"
            className={styles.deleteButton}
            data-testid={`${product.productExternalID}-delete`}
          >
            {i18n(`instructions:common.delete`)}
          </Button>
        )}
      </CardMedia>
      <CardContent>
        <Typography appearance="heading4" as="div" textAlign="center">
          {product.name}
        </Typography>
      </CardContent>
    </NavCard>
  );
}
